// @ts-nocheck
/**
 * Schema definition for ContentsPropertiesModal
 *
 * Defines the form schema for batch editing content properties,
 * including standard fields and batch relation fields.
 *
 * @module ContentsPropertiesModalSchema
 */
import { contentsPropertiesModalMessages as messages } from '@fhnw/messages/contentsPropertiesModal';

/**
 * Creates the base schema for the ContentsPropertiesModal form.
 *
 * @param {Object} options - Schema options
 * @param {Object} options.intl - react-intl instance for translations
 * @param {Object} options.mergedRemoveChoices - Merged choices for remove dropdowns
 * @returns {Object} Form schema object
 */
export const createBaseSchema = ({ intl, mergedRemoveChoices }) => ({
  fieldsets: [
    {
      id: 'default',
      title: intl.formatMessage(messages.default),
      fields: [
        'effective',
        'expires',
        'rights',
        'creators',
        'exclude_from_nav',
        'link',
      ],
    },
    {
      id: 'relations',
      title: intl.formatMessage(messages.batchProcessing),
      fields: [
        'universities_to_add',
        'universities_to_remove',
        'organisation_entities_to_add',
        'organisation_entities_to_remove',
        'topics_to_add',
        'topics_to_remove',
        'research_areas_to_add',
        'research_areas_to_remove',
        'future_fields_to_add',
        'future_fields_to_remove',
      ],
    },
  ],
  properties: {
    // Standard fields
    effective: {
      description: intl.formatMessage(messages.effectiveDescription),
      title: intl.formatMessage(messages.effectiveTitle),
      type: 'string',
      widget: 'datetime',
    },
    expires: {
      description: intl.formatMessage(messages.expiresDescription),
      title: intl.formatMessage(messages.expiresTitle),
      type: 'string',
      widget: 'datetime',
    },
    rights: {
      description: intl.formatMessage(messages.rightsDescription),
      title: intl.formatMessage(messages.rightsTitle),
      type: 'string',
      widget: 'textarea',
    },
    creators: {
      description: intl.formatMessage(messages.creatorsDescription),
      title: intl.formatMessage(messages.creatorsTitle),
      type: 'array',
    },
    exclude_from_nav: {
      description: intl.formatMessage(messages.excludeFromNavDescription),
      title: intl.formatMessage(messages.excludeFromNavTitle),
      type: 'boolean',
    },
    link: {
      title: intl.formatMessage(messages.link),
      type: 'string',
      widget: 'uri',
    },

    // Batch fields for Relations (Add/Remove)
    // Using 'array' widget instead of 'token' because:
    // - TokenWidget returns item.label (title)
    // - ArrayWidget returns item.value (UID/token)
    // The backend expects UIDs for RelationList fields

    // Universities - Add
    universities_to_add: {
      title: intl.formatMessage(messages.universitiesToAdd),
      description: intl.formatMessage(messages.universitiesToAddDescription),
      type: 'array',
      widget: 'array',
      items: {
        vocabulary: { '@id': 'fhnw.vocabularies.university' },
      },
    },
    // Universities - Remove (uses actual stored values, not vocabulary)
    universities_to_remove: {
      title: intl.formatMessage(messages.universitiesToRemove),
      description: intl.formatMessage(messages.universitiesToRemoveDescription),
      type: 'array',
      widget: 'array',
      choices: mergedRemoveChoices.universities || [],
    },

    // Organisation entities - Add
    organisation_entities_to_add: {
      title: intl.formatMessage(messages.organisationEntitiesToAdd),
      description: intl.formatMessage(
        messages.organisationEntitiesToAddDescription,
      ),
      type: 'array',
      widget: 'array',
      items: {
        vocabulary: { '@id': 'fhnw.vocabularies.organisation_entities' },
      },
    },
    // Organisation entities - Remove (uses actual stored values, not vocabulary)
    organisation_entities_to_remove: {
      title: intl.formatMessage(messages.organisationEntitiesToRemove),
      description: intl.formatMessage(
        messages.organisationEntitiesToRemoveDescription,
      ),
      type: 'array',
      widget: 'array',
      choices: mergedRemoveChoices.organisation_entities || [],
    },

    // Topics - Add
    topics_to_add: {
      title: intl.formatMessage(messages.topicsToAdd),
      description: intl.formatMessage(messages.topicsToAddDescription),
      type: 'array',
      widget: 'array',
      items: {
        vocabulary: { '@id': 'fhnw.vocabularies.topic' },
      },
    },
    // Topics - Remove (uses actual stored values, not vocabulary)
    topics_to_remove: {
      title: intl.formatMessage(messages.topicsToRemove),
      description: intl.formatMessage(messages.topicsToRemoveDescription),
      type: 'array',
      widget: 'array',
      choices: mergedRemoveChoices.topics || [],
    },

    // Research areas - Add
    research_areas_to_add: {
      title: intl.formatMessage(messages.researchAreasToAdd),
      description: intl.formatMessage(messages.researchAreasToAddDescription),
      type: 'array',
      widget: 'array',
      items: {
        vocabulary: { '@id': 'fhnw.vocabularies.research_area' },
      },
    },
    // Research areas - Remove (uses actual stored values, not vocabulary)
    research_areas_to_remove: {
      title: intl.formatMessage(messages.researchAreasToRemove),
      description: intl.formatMessage(
        messages.researchAreasToRemoveDescription,
      ),
      type: 'array',
      widget: 'array',
      choices: mergedRemoveChoices.research_areas || [],
    },

    // Future fields - Add
    future_fields_to_add: {
      title: intl.formatMessage(messages.futureFieldsToAdd),
      description: intl.formatMessage(messages.futureFieldsToAddDescription),
      type: 'array',
      widget: 'array',
      items: {
        vocabulary: { '@id': 'fhnw.vocabularies.future_field' },
      },
    },
    // Future fields - Remove (labels resolved via vocabulary in the modal)
    future_fields_to_remove: {
      title: intl.formatMessage(messages.futureFieldsToRemove),
      description: intl.formatMessage(messages.futureFieldsToRemoveDescription),
      type: 'array',
      widget: 'array',
      choices: mergedRemoveChoices.future_fields || [],
    },
  },
  required: [],
});
